import { useEffect, useRef, useState } from 'react';
import { supabase } from '../services/supabase';

/**
 * The claim bar (claim from the page, JT 27 Oct 2026; design Nicky 28 Oct
 * 2026): a full-width "Claim my page" button inside a full-width card with
 * a highlighted edge, and "No monthly fees · No credit card · Takes 1 minute"
 * beneath it on one line, sized to fit. No greeting.
 *
 * On an UNCLAIMED shoutout.fans page, while the page_claims switch is on, a
 * bar at the bottom offers the page to its creator. Tapping it opens Start on
 * creator.shoutout.us with ?claim=<slug>; once they sign up or sign in the
 * page is theirs (page_claim_start). On those pages the "Become a Fan" bar and
 * popup stand aside.
 *
 * In qa mode the bar shows only with ?claimqa=1 in the address (staff and QA
 * testers can then claim; the server checks who they are).
 *
 * Three funnel events (page_claim_track; the server keeps one per visitor per
 * page per day): claim_page_opened, claim_bar_seen, claim_bar_tapped.
 */

export const PAGE_CLAIM_COPY = {
  /** Always one line: its size scales down to fit a narrow phone. */
  subline: 'No monthly fees · No credit card · Takes 1 minute',
  button: 'Claim my page',
} as const;

export const PAGE_CLAIM_START_URL = 'https://creator.shoutout.us/start';

export function pageClaimStartHref(slug: string): string {
  return `${PAGE_CLAIM_START_URL}?claim=${encodeURIComponent(slug.toLowerCase())}`;
}

export type PageClaimBarStatus = 'loading' | 'show' | 'hide';

type PublicState = { mode?: unknown; claimable?: unknown };

/** Whether the qa preview is asked for in the address (?claimqa=1). */
function qaPreviewRequested(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('claimqa') === '1';
  } catch {
    return false;
  }
}

/** Only on the page itself (/:username), never on its sub-pages. */
function onPageRoot(): boolean {
  if (typeof window === 'undefined') return false;
  return (window.location.pathname || '').split('/').filter(Boolean).length === 1;
}

type ClaimEvent = 'claim_page_opened' | 'claim_bar_seen' | 'claim_bar_tapped';

function track(talentId: string, event: ClaimEvent): Promise<void> {
  return Promise.resolve(supabase.rpc('page_claim_track', { p_talent_id: talentId, p_event: event }))
    .then(({ error }) => {
      if (error) console.warn('page_claim_track failed:', error.message);
    })
    .catch(() => undefined);
}

/** The tap is recorded before the page leaves (at most a short wait). */
export async function trackTapThenGo(talentId: string, href: string, waitMs = 600): Promise<void> {
  await Promise.race([track(talentId, 'claim_bar_tapped'), new Promise((r) => setTimeout(r, waitMs))]);
  window.location.assign(href);
}

/**
 * @param ownerId the page's owner (talent_profiles.user_id); an owned page
 *   never shows the bar and never asks the server.
 * @param embedded the page is inside an iframe or an embed preview.
 */
export function usePageClaimBar(
  talentId: string | null | undefined,
  ownerId: string | null | undefined,
  embedded: boolean
): PageClaimBarStatus {
  const owned = !!ownerId;
  const eligible = !!talentId && !owned && !embedded && onPageRoot();
  // The answer, and which page it is for: until this page's answer is in, an
  // eligible page reads 'loading' (never a stale 'hide' that would let the
  // Become-a-Fan popup start for one render).
  const [answer, setAnswer] = useState<{ forId: string; status: PageClaimBarStatus } | null>(null);
  const tracked = useRef<string | null>(null);

  useEffect(() => {
    if (!eligible || !talentId) return undefined;
    let live = true;
    void (async () => {
      let show = false;
      try {
        const { data, error } = await supabase.rpc('page_claim_public_state', { p_talent_id: talentId });
        const d = (data ?? {}) as PublicState;
        const on = d.mode === 'on' || (d.mode === 'qa' && qaPreviewRequested());
        show = !error && on && d.claimable === true;
      } catch {
        show = false;
      }
      if (!live) return;
      setAnswer({ forId: talentId, status: show ? 'show' : 'hide' });
      if (show && tracked.current !== talentId) {
        tracked.current = talentId;
        void track(talentId, 'claim_page_opened');
        void track(talentId, 'claim_bar_seen');
      }
    })();
    return () => {
      live = false;
    };
  }, [talentId, eligible]);

  if (!eligible || !talentId) return 'hide';
  return answer && answer.forId === talentId ? answer.status : 'loading';
}

export function PageClaimBar({
  talentId,
  slug,
  lightBackground,
}: {
  talentId: string;
  slug: string;
  /** The page's background is light: a dark bar reads better. */
  lightBackground: boolean;
}) {
  const href = pageClaimStartHref(slug);
  // The bar is fixed; a spacer of its real height (safe area included) keeps
  // the page's last lines, the footer, scrollable above it on any phone.
  const barRef = useRef<HTMLDivElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  useEffect(() => {
    const el = barRef.current;
    if (!el) return undefined;
    const measure = () => setBarHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <>
      <div aria-hidden="true" style={{ height: barHeight }} data-page-claim-bar-spacer />
      <div
        ref={barRef}
        className="fixed inset-x-0 bottom-0 z-40 px-3 pt-2"
        style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}
        data-page-claim-bar
      >
        <div
          className="rounded-2xl p-3 shadow-2xl backdrop-blur-xl"
          style={{
            backgroundColor: lightBackground ? 'rgba(17,17,17,0.9)' : 'rgba(255,255,255,0.12)',
            // the highlighted edge: the button's green, with a soft glow
            boxShadow: '0 0 0 2px #10b981, 0 0 24px rgba(16,185,129,0.45)',
          }}
        >
          <a
            href={href}
            onClick={(e) => {
              // a plain tap: record it, then go (a new-tab click just goes)
              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
              e.preventDefault();
              void trackTapThenGo(talentId, href);
            }}
            className="flex min-h-[48px] w-full items-center justify-center whitespace-nowrap rounded-full px-5 text-base font-semibold text-white"
            style={{ background: 'linear-gradient(90deg, #10b981, #06b6d4)' }}
          >
            {PAGE_CLAIM_COPY.button}
          </a>
          <p
            className="mt-2 whitespace-nowrap text-center leading-snug text-white/70"
            // one line on any width: 12px, or smaller when the card is narrower
            // than the line (the card's inside is the viewport less 48px)
            style={{ fontSize: 'min(0.75rem, calc((100vw - 48px) / 27))' }}
          >
            {PAGE_CLAIM_COPY.subline}
          </p>
        </div>
      </div>
    </>
  );
}
